import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';

import moment from 'moment-timezone';
import { Subscription } from 'rxjs';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { BlockedTime } from '@shared/blocked-time/models';
import { Service } from '@shared/service/models';
import { Resource } from '@shared/resource/models';
import {
  BlockedTimeScope,
  TimeOfDay,
  blockedTimesIntersect,
  msFromMidnightToTimeOfDay,
  timeOfDayToMsFromMidnight,
} from '@shared/blocked-time/functions';
import {
  CustomDateParserFormatterProvider,
  DateTimeService,
} from '@tablesready/common-ui';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';

export interface BlockedTimeModalResult {
  action: 'save' | 'delete';
  blockedTime?: Partial<BlockedTime>;
}

interface BlockedTimeFormControls {
  _id: FormControl<string | undefined>;
  name: FormControl<string>;
  date: FormControl<NgbDateStruct | null>;
  startTime: FormControl<TimeOfDay | null>;
  endTime: FormControl<TimeOfDay | null>;
  allServices: FormControl<boolean>;
  services: FormControl<string[]>;
  resources: FormControl<string[]>;
  reason: FormControl<string>;
}

function startBeforeEndValidator(group: FormGroup<BlockedTimeFormControls>): ValidationErrors | null {
  const start = group.controls.startTime.value;
  const end = group.controls.endTime.value;
  if (!start || !end) {
    return null;
  }

  return timeOfDayToMsFromMidnight(start) < timeOfDayToMsFromMidnight(end)
    ? null
    : { startBeforeEnd: true };
}

function servicesRequiredValidator(group: FormGroup<BlockedTimeFormControls>): ValidationErrors | null {
  const allServices = group.controls.allServices.value;
  const services = group.controls.services.value;
  if (allServices || services.length) {
    return null;
  }

  return { servicesRequired: true };
}

@Component({
  selector: 'tablesready-add-edit-blocked-time',
  templateUrl: './add-edit-blocked-time.component.html',
  styleUrls: ['./add-edit-blocked-time.component.scss'],
  standalone: false,
  providers: [CustomDateParserFormatterProvider],
})
export class AddEditBlockedTimeComponent implements OnInit, OnDestroy {
  @Input() blockedTime: Partial<BlockedTime> = {};
  @Input() services: Service[] = [];
  @Input() resources: Resource[] = [];
  @Input() blockedTimes: BlockedTime[] = [];
  @Input() timezone: string;

  icon = AppIcon;
  title: string;
  form: FormGroup<BlockedTimeFormControls>;
  minDate: NgbDateStruct;
  enabledServices: Service[] = [];
  filteredResources: Resource[] = [];

  private readonly subscription = new Subscription();

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly dateTimeService: DateTimeService,
  ) {}

  ngOnInit(): void {
    this.title = this.blockedTime._id ? 'Edit' : 'Add';
    this.minDate = this.computeMinDate();
    this.enabledServices = this.services.filter(service => service.enabled);

    this.form = new FormGroup<BlockedTimeFormControls>(
      {
        _id: new FormControl(this.blockedTime._id),
        name: new FormControl(this.blockedTime.name ?? '', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        date: new FormControl(this.toNgbDate(this.blockedTime.date), { validators: [Validators.required] }),
        startTime: new FormControl(this.timeToModel(this.blockedTime.startTime), { validators: [Validators.required] }),
        endTime: new FormControl(this.timeToModel(this.blockedTime.endTime), { validators: [Validators.required] }),
        allServices: new FormControl(this.blockedTime.allServices ?? false, { nonNullable: true }),
        services: new FormControl(this.blockedTime.services ?? [], { nonNullable: true }),
        resources: new FormControl(this.blockedTime.resources ?? [], { nonNullable: true }),
        reason: new FormControl(this.blockedTime.reason ?? '', { nonNullable: true }),
      },
      { validators: [startBeforeEndValidator, servicesRequiredValidator, this.overlapValidator] },
    );

    this.filteredResources = this.computeFilteredResources();

    this.subscription.add(
      this.form.controls.services.valueChanges.subscribe(() => {
        this.filteredResources = this.computeFilteredResources();
        this.pruneOrphanedResources();
      }),
    );

    this.pruneDisabledSelections();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private pruneOrphanedResources(): void {
    const validResourceIds = new Set(this.filteredResources.map(resource => resource._id));
    const selectedResources = this.form.controls.resources.value;
    const keptResources = selectedResources.filter(resourceId => validResourceIds.has(resourceId));

    if (keptResources.length !== selectedResources.length) {
      this.form.controls.resources.setValue(keptResources);
    }
  }

  private pruneDisabledSelections(): void {
    const enabledServiceIds = new Set(this.enabledServices.map(service => service._id));
    const selectedServices = this.form.controls.services.value;
    const keptServices = selectedServices.filter(serviceId => enabledServiceIds.has(serviceId));

    if (keptServices.length !== selectedServices.length) {
      this.form.controls.services.setValue(keptServices);

      return;
    }

    this.pruneOrphanedResources();
  }

  private readonly overlapValidator = (group: FormGroup<BlockedTimeFormControls>): ValidationErrors | null => {
    const scope = this.buildScope(group);
    if (!scope) {
      return null;
    }

    const overlaps = this.blockedTimes.some(existing =>
      existing._id !== this.blockedTime._id && blockedTimesIntersect(scope, existing));

    return overlaps ? { blockedTimeOverlap: true } : null;
  };

  private buildScope(group: FormGroup<BlockedTimeFormControls>): BlockedTimeScope | null {
    const value = group.getRawValue();
    if (!value.date || !value.startTime || !value.endTime) {
      return null;
    }

    return {
      date: this.dateTimeService.ngbDateToMoment(value.date, this.timezone).startOf('day')
        .valueOf(),
      startTime: timeOfDayToMsFromMidnight(value.startTime),
      endTime: timeOfDayToMsFromMidnight(value.endTime),
      allServices: value.allServices,
      services: value.allServices ? [] : value.services,
      resources: value.allServices ? [] : value.resources ?? [],
    };
  }

  get allServices(): boolean {
    return this.form.controls.allServices.value;
  }

  get isEdit(): boolean {
    return Boolean(this.blockedTime._id);
  }

  private computeFilteredResources(): Resource[] {
    const selectedServices = this.form.controls.services.value;
    if (!selectedServices.length) {
      return [];
    }

    return this.resources
      .filter(resource => resource.enabled
        && resource.services?.some(serviceId => selectedServices.includes(serviceId)));
  }

  confirm(): void {
    const value = this.form.getRawValue();
    const scope = this.buildScope(this.form);
    if (!scope) {
      return;
    }

    const payload: Partial<BlockedTime> = {
      name: value.name,
      ...scope,
      reason: value.reason || undefined,
    };

    if (value._id) {
      payload._id = value._id;
    }

    this.modalController.dismiss({
      action: 'save',
      blockedTime: payload,
    });
  }

  delete(): void {
    this.modalController.dismiss({ action: 'delete' });
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  private computeMinDate(): NgbDateStruct {
    const today = moment.tz(this.timezone).startOf('day');
    const blockDate = this.blockedTime.date
      ? moment.tz(this.blockedTime.date, this.timezone).startOf('day')
      : null;
    const min = blockDate && blockDate.isBefore(today) ? blockDate : today;

    return this.dateTimeService.momentToNgbDate(min);
  }

  private toNgbDate(date?: number): NgbDateStruct {
    const dateMoment = date ? moment.tz(date, this.timezone) : moment.tz(this.timezone);

    return this.dateTimeService.momentToNgbDate(dateMoment);
  }

  private timeToModel(msFromMidnight?: number): TimeOfDay | null {
    if (msFromMidnight === undefined) {
      return null;
    }

    return msFromMidnightToTimeOfDay(msFromMidnight);
  }
}
